import React, { useState } from "react";
import { graphql } from "gatsby";
import Project, { ProjectYamlType } from "~/models/project";
import SEO from "~/views/components/generic/seo";
import structuredData from "~/helpers/structured-data";
import Frame from "~/views/layout/frame";
import ProjectCard from "~/views/components/project/card";
import Navigation from "~/views/compositions/navigation";
import Breadcrumbs from "~/views/compositions/breadcrumbs";
import Page from "~/models/page";

type Props = {
  data: {
    projects: {
      nodes: ProjectYamlType[];
      pageInfo: {
        currentPage: number;
        hasNextPage: boolean;
        hasPreviousPage: boolean;
        pageCount: number;
        itemCount: number;
        perPage: number;
        totalCount: number;
      };
    };
  };
};

const ProjectListPage = ({ data }: Props) => {
  const page = new Page(data.page);
  const defaultPageInfo = data.projects.pageInfo;
  const defaultProjects = data.projects.nodes.map(
    (projectData: ProjectYamlType) => new Project(projectData)
  );

  const [projects, setProjects] = useState(defaultProjects);
  const [pageInfo, setPageInfo] = useState(defaultPageInfo);

  const onLoadMore = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    try {
      const response = await fetch(
        `/page-data/projects/pages/${pageInfo.currentPage + 1}/page-data.json`
      ).then((response) => response.json());

      const nextProjects = response.result.data.projects.nodes.map(
        (projectData: ProjectYamlType) => new Project(projectData)
      );

      setProjects(projects.concat(nextProjects));
      setPageInfo(response.result.data.projects.pageInfo);
    } catch (error) {}
  };

  return (
    <Frame>
      <Navigation />
      <Breadcrumbs urls={page.breadcrumb} />

      {/* Header Section */}
      <section className="flex flex-col gap-8 py-5 w-full mx-auto px-container">
        <div className="flex flex-col gap-4">
          <h1 className="font-[800] text-[40px] leading-[74px] palt">
            Projects & Works
          </h1>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="pb-20 max-w-[1096px] mx-auto">
        <div className="grid grid-cols-2 gap-0">
          {projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              className="max-w-full"
            />
          ))}
        </div>

        {/* Load More Button */}
        {pageInfo.hasNextPage && (
          <div className="flex justify-center">
            <button
              onClick={onLoadMore}
              className="inline-flex items-center gap-2.5 px-8 py-4 border border-black rounded-[30px] bg-white hover:bg-neutral-99 transition-colors"
            >
              <span className="font-medium text-lg">さらに読み込む</span>
            </button>
          </div>
        )}
      </section>
    </Frame>
  );
};

export default ProjectListPage;

export const Head = ({ data }: any) => {
  const page = new Page(data.page);
  const defaultPageInfo = data.projects.pageInfo;
  const sd = structuredData(page);

  if (defaultPageInfo.currentPage > 1) {
    page.url.pathname = `/projects/pages/${data.projects.pageInfo.currentPage}/`;
  }

  return (
    <SEO page={page} sd={sd}>
      {defaultPageInfo.hasPreviousPage && (
        <link
          rel="prev"
          href={
            defaultPageInfo.currentPage == 2
              ? `${process.env.GATSBY_SITE_URL}/projects/`
              : `${process.env.GATSBY_SITE_URL}/projects/pages/${
                  defaultPageInfo.currentPage - 1
                }/`
          }
        />
      )}

      {defaultPageInfo.hasNextPage && (
        <link
          rel="next"
          href={`${process.env.GATSBY_SITE_URL}/projects/pages/${
            defaultPageInfo.currentPage + 1
          }/`}
        />
      )}
    </SEO>
  );
};

export const query = graphql`
  query ProjectListPage($skip: Int, $limit: Int) {
    page: pageMdx(
      filter: { frontmatter: { url: { pathname: { eq: "/projects/" } } } }
    ) {
      id
      frontmatter {
        id
        title
        description
        breadcrumb {
          ...UrlFields
        }
        url {
          ...UrlFields
        }
        type
        releasedAt
        updatedAt
      }
    }
    projects: allProjectYaml(
      filter: { sys: { status: { eq: "published" } } }
      sort: { order: DESC }
      skip: $skip
      limit: $limit
    ) {
      nodes {
        ...ProjectFields
      }
      pageInfo {
        currentPage
        hasNextPage
        hasPreviousPage
        pageCount
        itemCount
        perPage
        totalCount
      }
    }
  }
`;
